import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Heading,
    Layer,
    RadioButtonGroup,
    Text,
    TextArea,
    TextInput,
    Tip,
} from 'grommet';
import { Chat } from 'grommet-icons';
import { API } from '../api';
import AppConstants from '../helpers/AppConstants';
import { Utils } from '../helpers/Utils';

const MessageModal = ({ contact, setShowModal, fromCompany }) => {
    contact = Utils.deepFlatten(contact);
    const [messageType, setMessageType] = useState('sms');
    const [messageContent, setMessageContent] = useState('');
    const [emailSubject, setEmailSubject] = useState(
        fromCompany ? `Message from ${fromCompany}` : '',
    );
    const [buttonBusy, setButtonBusy] = useState(false);
    const [notificationVisible, setNotificationVisible] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    const sendMessage = async () => {
        setButtonBusy(true);
        try {
            await API.post('/api/messages', {
                data: {
                    content: messageContent,
                    ...(contact.user && { recipients: [contact.user.id] }),
                    recipientEmails: [contact.email],
                    sender: localStorage.getItem(AppConstants.cacheKeys.userId),
                    ...(emailSubject && { subject: emailSubject }),
                    type: messageType,
                    ...(fromCompany && { fromCompany }),
                },
            });
            setShowModal(false);
        } catch (error) {
            console.log(error);
            setNotificationMessage(error.message);
            setNotificationVisible(true);
        }

        setButtonBusy(false);
    };

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="small">
                <Heading level="3">
                    Send message to {contact.firstName} {contact.lastName}
                </Heading>
                <Box gap="medium">
                    <RadioButtonGroup
                        name="messageType"
                        options={[
                            { label: 'SMS', value: 'sms' },
                            { label: 'Email', value: 'email' },
                        ]}
                        value={messageType}
                        onChange={(event) => setMessageType(event.target.value)}
                    />
                    {messageType == 'email' && (
                        <Box margin={{ vertical: 'small' }}>
                            <TextInput
                                placeholder="Subject"
                                value={emailSubject}
                                disabled={!!fromCompany}
                                onChange={(event) =>
                                    setEmailSubject(event.target.value)
                                }
                            />
                        </Box>
                    )}
                    {fromCompany && (
                        <TextInput
                            value={`(FROM ${fromCompany})`}
                            disabled
                        />
                    )}
                    <TextArea
                        placeholder="Message"
                        value={messageContent}
                        onChange={(event) =>
                            setMessageContent(event.target.value)
                        }
                    />
                </Box>

                <Box align="end">
                    <Button
                        primary
                        size="large"
                        label="Send"
                        onClick={() => sendMessage()}
                        busy={buttonBusy}
                    />
                </Box>
                {notificationVisible && (
                    <Box>
                        <Text color="red">{notificationMessage}</Text>
                    </Box>
                )}
            </Box>
        </Layer>
    );
};

const MessageButton = ({ contact, fromCompany, iconOnly = false }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <Box>
            {iconOnly ? (
                <Tip content="Send message">
                    <Button
                        icon={<Chat />}
                        a11yTitle="Send message"
                        onClick={() => setShowModal(true)}
                    />
                </Tip>
            ) : (
                <Button
                    primary
                    label="Send message"
                    size="large"
                    onClick={() => setShowModal(true)}
                />
            )}
            {showModal && (
                <MessageModal
                    contact={contact}
                    setShowModal={setShowModal}
                    fromCompany={fromCompany}
                />
            )}
        </Box>
    );
};

MessageModal.propTypes = {
    contact: PropTypes.object,
    setShowModal: PropTypes.func,
    fromCompany: PropTypes.string,
};

MessageButton.propTypes = {
    contact: PropTypes.object,
    fromCompany: PropTypes.string,
    iconOnly: PropTypes.bool,
};

export { MessageButton, MessageModal };
